import React, { Component } from 'react';
import PropTypes from 'prop-types';

import MultiSelect from 'material-ui-superselectfield';
import FlatButton from 'material-ui/FlatButton';
import Chip from 'material-ui/Chip/Chip';

import { colors } from 'helpers/ColorHelper';
import { localize } from 'react-localize-redux';

class MultiSelectExtended extends Component {
  /*
   * Multiselect with the ability to create a new entity. redux-form compatible.
   * If not using redux-form then the component will manage its own state.
   * See properties/addProperty/OwnerDetails.js for redux example
   * See chat/PropertyNotificationDialog.js for non-redux example
   *
   * props: Accepts all props that multiselect can take plus the following additional props.
   *
   * data (array): This is the data that super multiselect expects in the form:
   *   { value: value, label: label, [type]: type }
   *   - If prop grouped is set to true then the data will be rendered grouped on 'type'
   *
   * onChange (function): onChange function that will be called with the updaed state of the multiselect
   *
   * allowNone (boolean): Defaults to false. If set then an option for "None" is included
   *
   * grouped (boolean): Defaults to false. If set then the data will be rendered by grouping types
   *
   * createText (string): If this prop is passed then the component will be rendered with
   *   the { label: createText, value: createText } as the first item in the list.
   *
   * createComponent (component): Needs to be passed if using createText. This is the
   *   component that will be rendered when createText is selected
   *
   * rowRenderCallback (function): Optional function used to override the string rendered for each row.
       This can be used as a view layer without having to modify the underlying data
   *
   * fireOnChangeIfBlank (boolean): Defaults to false. If set, then the onchange callback will be fired
   *   if the resulting value is blank.
   */

  constructor(props) {
    super(props);

    const usingRedux = props.input ? true : false;

    this.state = {
      selection: props.multiple ? [] : { value: null, label: null },
      usingRedux,
    };
  }

  componentDidMount() {
    if (this.state.usingRedux) {
      this.setState({
        selection: Array.isArray(this.props.input.value) ? this.props.input.value : [],
      });
    }
  }

  componentDidUpdate(prevProps, prevState) {
    // If input has updated to [] and selection is not [] (i.e. reset), update it to []
    // Only do this if we are using redux
    if (!this.state.usingRedux) return;
    if (Array.isArray(prevProps.input.value) && prevProps.input.value.length === 0) return;

    const { input } = this.props;
    const { selection } = this.state;

    const newInputIsEmptyArray = Array.isArray(input.value) && input.value.length === 0;
    const selectionIsNotEmptyArray = !Array.isArray(selection) || selection.length !== 0;
    if (newInputIsEmptyArray && selectionIsNotEmptyArray) {
      this.setState({
        selection: [],
      });
    }
  }

  renderCreateComponent = (createText, createComponent) => {
    if (!createText) return;

    let selection = JSON.parse(JSON.stringify(this.state.selection));
    selection = !Array.isArray(selection) ? [selection] : selection;

    return selection.map(i => i.value).includes(createText) ? createComponent : undefined;
  };

  handleSelect = values => {
    if (!values && !this.props.fireOnChangeIfBlank) return;

    this.setState({ selection: values });

    // call onChange props if passed
    if (this.props.onChange) this.props.onChange(values);

    // update redux if used
    if (this.state.usingRedux) {
      this.props.input.onChange(values);
      this.props.input.onBlur(values);
    }
  };

  onRequestDelete = key => event => {
    if (this.props.disabled) return;
    // update the redux state if needed
    if (this.state.usingRedux) {
      this.props.input.onChange(this.props.input.value.filter((v, i) => i !== key));
    } else {
      this.props.onChange(this.state.selection.filter((v, i) => i !== key));
    }

    this.setState({ selection: this.state.selection.filter((v, i) => i !== key) });
  };

  handleCustomDisplaySelections = values =>
    values && values.length ? (
      <div style={{ display: 'flex', flexWrap: 'wrap' }}>
        {values.length <= 1 &&
          values.map((obj, index) => (
            <Chip
              key={index}
              style={{ margin: 5, fontSize: '14px', backgroundColor: '#f5f5f5' }}
              onRequestDelete={this.onRequestDelete(index)}
            >
              {this.renderRowText(obj)}
            </Chip>
          ))}

        {values.length > 1 && (
          <div
            style={{
              minHeight: this.props.minHeight || 42,
              lineHeight: this.props.minHeight || '42px',
            }}
          >
            {values.length} {this.props.translate('multiselectExtended.itemsSelected')}
          </div>
        )}
      </div>
    ) : (
      <div
        style={{
          minHeight: this.props.minHeight || 42,
          lineHeight: this.props.minHeight || '42px',
        }}
      >
        {this.props.hintText}
      </div>
    );

  // helper used to get unique group types from data
  uniqueGroups = () => {
    if (!this.props.grouped || !Array.isArray(this.props.data)) return [];

    // get all unique types
    const types = new Set();
    if (this.props.allowNone || this.props.createText) {
      // '' is used to group and included addon types such as 'None' or 'Create Component'
      types.add('');
    }
    this.props.data.forEach(element => types.add(element.type));
    return Array.from(types); // convert Set of types to array
  };

  // helper used to create dropdown options
  buildDropdownOptions = options => {
    if (!Array.isArray(options)) return [];

    let menuItemStyle = {
      whiteSpace: 'normal',
      display: 'flex',
      justifyContent: 'space-between',
      lineHeight: 'normal',
      fontSize: '14px',
      minHeight: '18px',
    };

    return options.map((option, index) => {
      if (this.props.grouped && option.type !== '') {
        menuItemStyle = { ...menuItemStyle, marginLeft: '10px', fontSize: '14px' };
      }

      return (
        <div key={index} value={option.value} label={option.label} style={menuItemStyle}>
          <span>{this.renderRowText(option)}</span>
        </div>
      );
    });
  };

  dropdownOptions = options => {
    options = !Array.isArray(options) ? [] : options;

    if (this.props.createText) {
      options = [
        { label: this.props.createText, value: this.props.createText, type: '' },
        ...options,
      ];
    }

    if (this.props.allowNone) {
      options = [{ label: 'None', value: 'None', type: '' }, ...options];
    }

    if (this.props.grouped) {
      return this.uniqueGroups().map((type, index) => {
        const itemsOfGroupType = options.filter(item => item.type === type);
        return (
          <optgroup key={index} label={type}>
            {this.buildDropdownOptions(itemsOfGroupType)}
          </optgroup>
        );
      });
    } else {
      return this.buildDropdownOptions(options);
    }
  };

  renderRowText = value => {
    if (this.props.rowRenderCallback) {
      return this.props.rowRenderCallback(value);
    }

    return value.label;
  };

  render() {
    const {
      meta,
      onChange,
      createComponent,
      createText,
      data,
      input,
      hintTextAutocomplete,
      elementHeight,
      hintText,
      translate,
      ...otherProps
    } = this.props;

    const options = data || this.state.selection;

    let selectionsRenderer;
    if (this.props.multiple) {
      selectionsRenderer = this.handleCustomDisplaySelections;
    }

    const errorText =
      meta && meta.touched && (meta.error || meta.warning) ? meta.error || meta.warning : '';

    return (
      <div>
        <MultiSelect
          value={this.state.selection}
          showAutocompleteThreshold={this.props.autocompleteThreshold || 1000}
          hintTextAutocomplete={hintTextAutocomplete || translate('multiselectExtended.search')}
          onChange={this.handleSelect}
          checkPosition="left"
          hintText={hintText || ''}
          selectionsRenderer={selectionsRenderer}
          useLayerForClickAway={true}
          errorText={errorText}
          elementHeight={39}
          popoverWidth={310}
          selectAllButton={
            <FlatButton
              label={translate('multiselectExtended.selectAll')}
              hoverColor={colors.LIGHT_GRAY}
              fullWidth
              labelStyle={{ whiteSpace: 'nowrap' }}
            />
          }
          resetButton={
            <FlatButton
              label={translate('multiselectExtended.reset')}
              hoverColor={colors.LIGHT_GRAY}
              fullWidth
            />
          }
          menuCloseButton={
            <FlatButton
              label={translate('multiselectExtended.close')}
              hoverColor={colors.LIGHT_GRAY}
              style={{ marginRight: '6px' }}
            />
          }
          {...otherProps}
        >
          {this.dropdownOptions(options)}
        </MultiSelect>

        {this.renderCreateComponent(createText, createComponent)}
      </div>
    );
  }
}

MultiSelectExtended.propTypes = {
  data: PropTypes.array.isRequired,
  onChange: PropTypes.func,
  allowNone: PropTypes.bool,
  grouped: PropTypes.bool,
  createText: PropTypes.string,
  createComponent: PropTypes.node,
};

export default localize()(MultiSelectExtended);
